import React, { useEffect, useMemo, useState } from 'react';
import {
  LuArrowLeft,
  LuArrowRightLeft,
  LuBookOpenText,
  LuBot,
  LuChartBar,
  LuLoaderPinwheel,
  LuPencil,
  LuPlus,
  LuSearch,
  LuSettings,
  LuTrash2,
  LuX,
} from 'react-icons/lu';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { StandaloneProject } from '../../entities/standalone-project';
import { StandaloneTask } from '../../entities/standalone-task';
import {
  createStandaloneTask,
  deleteStandaloneProject,
  deleteStandaloneTask,
  getStandaloneProject,
  getStandaloneTask,
  listStandaloneTasks,
  reorderStandaloneTasks,
  standaloneErrorMessage,
  updateStandaloneProject,
  updateStandaloneTask,
} from '../../shared/api/standalone';
import { useAIChat } from '../../shared/contexts/AIChat.context';
import { auth } from '../../shared/firebase/firebase';
import useSocket from '../../shared/hooks/socket-hook';
import { invalidateStandaloneProjectsCache } from '../../shared/hooks/use-standalone-projects';
import BoardSettingsModal from '../../widgets/standalone/BoardSettingsModal';
import TaskCard from '../../widgets/standalone/TaskCard';
import TaskModal, { TaskFormValues } from '../../widgets/standalone/TaskModal';
import TransferModal from '../../widgets/transfer/TransferModal';
import { ActionsMenu } from '../../widgets/ui/ActionsMenu';
import { Modal } from '../../widgets/ui/Modal';

const StandaloneBoardPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { openChat } = useAIChat();
  const { on, off } = useSocket(auth.currentUser?.uid || '');

  const [project, setProject] = useState<StandaloneProject | null>(null);
  const [tasks, setTasks] = useState<StandaloneTask[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // TEMP — FE-only client-side search. `listStandaloneTasks` has no pagination yet, so the full
  // task list is already in memory; this just filters it. Once BE adds pagination (and, likely,
  // a server-side search/filter param), this needs to become a real API call instead of a
  // substring match over everything the client happens to have loaded — flagged in the mflow
  // knowledge base (`__global__`-adjacent, project-scoped) so it isn't forgotten. Drag-and-drop
  // reordering is disabled while a search is active (see the `isSearching` guards below) — the
  // filtered view hides cards, and reordering against a partial view would produce a confusing
  // drop position.
  const [search, setSearch] = useState('');
  const isSearching = search.trim().length > 0;
  const matchesSearch = (t: StandaloneTask) => {
    if (!isSearching) return true;
    const q = search.trim().toLowerCase();
    return (
      t.title.toLowerCase().includes(q) ||
      (t.description ?? '').toLowerCase().includes(q) ||
      t.key.toLowerCase().includes(q)
    );
  };
  const matchCount = isSearching ? tasks.filter(matchesSearch).length : tasks.length;

  const [isEditingProject, setIsEditingProject] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [isSavingProject, setIsSavingProject] = useState(false);
  const [isDeletingProject, setIsDeletingProject] = useState(false);

  const [draggingTaskId, setDraggingTaskId] = useState<string | null>(null);
  const [dragOverTaskId, setDragOverTaskId] = useState<string | null>(null);
  const [dragOverPosition, setDragOverPosition] = useState<'before' | 'after' | null>(null);
  const [modalTask, setModalTask] = useState<StandaloneTask | null>(null);
  const [modalDefaultStatus, setModalDefaultStatus] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  // True only while re-fetching a task's full details for the edit modal (see openEditModal) —
  // never set for create mode, so it doubles as "is this an edit-in-flight" without a separate flag.
  const [isLoadingModalTask, setIsLoadingModalTask] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const statuses = useMemo(() => [...(project?.statuses ?? [])].sort((a, b) => a.order - b.order), [project]);
  // Subtask progress badge on a parent's card — computed once per render, not per-card.
  const subtaskStats = useMemo(() => {
    const stats = new Map<string, { total: number; done: number }>();
    const statusCategory = new Map(statuses.map((s) => [s.id, s.category]));
    tasks.forEach((t) => {
      if (!t.parentId) return;
      const entry = stats.get(t.parentId) ?? { total: 0, done: 0 };
      entry.total += 1;
      if (statusCategory.get(t.status) === 'done') entry.done += 1;
      stats.set(t.parentId, entry);
    });
    return stats;
  }, [tasks, statuses]);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    Promise.all([getStandaloneProject(projectId), listStandaloneTasks(projectId)])
      .then(([proj, taskList]) => {
        if (cancelled) return;
        setProject(proj);
        setTasks(taskList);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Failed to load project.');
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Dedupe-safe insert-or-replace by id — creating a task over REST also fires a
  // `standaloneTaskUpdated` WS echo (standalone-contract.md §0 "Live updates"), which can arrive
  // before or after the REST response resolves. Every path that adds/updates a task in local state
  // must go through this, or a race between the two ends up inserting the same task twice.
  const upsertTask = (task: StandaloneTask) => {
    setTasks((prev) =>
      prev.some((t) => t._id === task._id) ? prev.map((t) => (t._id === task._id ? task : t)) : [task, ...prev],
    );
  };

  // Live sync — e.g. the chat/agent path creating or editing a task while the board is open.
  // Not fired on delete (see standalone-contract.md §0); deletes stay refetch-on-own-action only.
  useEffect(() => {
    if (!projectId) return;
    const onTaskUpdated = (data: { userId: string; task: StandaloneTask }) => {
      if (data.task.projectId !== projectId) return;
      upsertTask(data.task);
    };
    on('standaloneTaskUpdated', onTaskUpdated);
    return () => off('standaloneTaskUpdated', onTaskUpdated as (...args: unknown[]) => void);
  }, [projectId, on, off]);

  // Live sync for drag-and-drop reordering from another tab/device — a pure position change, so
  // it fires its own event instead of standaloneTaskUpdated (see standalone-contract.md "Task
  // ordering"). Applying it after our own optimistic update is harmless (same end state).
  useEffect(() => {
    if (!projectId) return;
    const onTasksReordered = (data: { userId: string; projectId: string; status: string; tasks: StandaloneTask[] }) => {
      if (data.projectId !== projectId) return;
      setTasks((prev) => prev.map((t) => data.tasks.find((u) => u._id === t._id) ?? t));
    };
    on('standaloneTasksReordered', onTasksReordered);
    return () => off('standaloneTasksReordered', onTasksReordered as (...args: unknown[]) => void);
  }, [projectId, on, off]);

  // Live sync — e.g. a status's agentRole (MF-39) or name/color set via the chat agent, MCP, or
  // another tab while this board is open.
  useEffect(() => {
    if (!projectId) return;
    const onProjectUpdated = (data: { userId: string; project: StandaloneProject }) => {
      if (data.project._id !== projectId) return;
      setProject(data.project);
    };
    on('standaloneProjectUpdated', onProjectUpdated);
    return () => off('standaloneProjectUpdated', onProjectUpdated as (...args: unknown[]) => void);
  }, [projectId, on, off]);

  const openCreateModal = (status: string) => {
    setModalTask(null);
    setModalDefaultStatus(status);
    setIsLoadingModalTask(false);
    setIsModalOpen(true);
  };

  // Opens with the freshly-fetched full task, not the board-list item passed in — the list is
  // rendering data (title/labels/priority/etc. for the card), not necessarily the full record the
  // edit form needs (e.g. a full, untruncated description), and the two shouldn't be coupled.
  const openEditModal = async (task: StandaloneTask) => {
    setModalDefaultStatus(task.status);
    setIsModalOpen(true);
    setIsLoadingModalTask(true);
    try {
      const full = await getStandaloneTask(task._id);
      setModalTask(full);
    } catch (err) {
      setError(standaloneErrorMessage(err) ?? 'Failed to load task details.');
      setIsModalOpen(false);
    } finally {
      setIsLoadingModalTask(false);
    }
  };

  // A task opened from within the board (e.g. via drag/click) can carry ?task=<id> so a reload or
  // shared board link reopens the same task's modal — resolved here once tasks are loaded, then
  // the param is dropped so it doesn't linger in the URL. The standalone `/standalone/tasks/:key`
  // shareable-link route (StandaloneTaskDetailPage) is a separate, full-page destination now.
  useEffect(() => {
    const taskId = searchParams.get('task');
    if (!taskId || tasks.length === 0) return;
    const task = tasks.find((t) => t._id === taskId);
    if (task) openEditModal(task);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('task');
      return next;
    });
  }, [tasks]);

  // MF-39 — the "fix it" action on the coding-agent role warning (StandaloneTaskDetailPage/
  // TaskModal) links here with ?settings=1 to jump straight into the status-roles screen.
  useEffect(() => {
    if (!searchParams.get('settings')) return;
    setIsSettingsOpen(true);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('settings');
      return next;
    });
  }, [searchParams]);

  const handleSaveTask = async (values: TaskFormValues) => {
    if (!projectId) return;
    if (modalTask) {
      const updated = await updateStandaloneTask(modalTask._id, values);
      setTasks((prev) => prev.map((t) => (t._id === updated._id ? updated : t)));
    } else {
      const created = await createStandaloneTask(projectId, values);
      upsertTask(created);
    }
    setIsModalOpen(false);
  };

  const handleDeleteTask = async () => {
    if (!modalTask) return;
    await deleteStandaloneTask(modalTask._id);
    setTasks((prev) => prev.filter((t) => t._id !== modalTask._id));
    setIsModalOpen(false);
  };

  const handleQuickAddSubtask = async (title: string) => {
    if (!projectId || !modalTask) return;
    const created = await createStandaloneTask(projectId, { title, status: statuses[0]?.id, parentId: modalTask._id });
    upsertTask(created);
  };

  const columnTaskIds = (status: string) =>
    tasks
      .filter((t) => t.status === status)
      .sort((a, b) => a.order - b.order)
      .map((t) => t._id);

  const clearDragState = () => {
    setDraggingTaskId(null);
    setDragOverTaskId(null);
    setDragOverPosition(null);
  };

  // Shared by both column-background and card-position drops — `taskIds` is the complete new
  // order for `status` (standalone-contract.md "Task ordering"). Optimistic, with a revert on
  // failure; the eventual `standaloneTasksReordered` WS echo (ours or another tab's) just re-applies
  // the same values, which is harmless.
  const applyReorder = async (status: string, taskIds: string[]) => {
    if (!projectId) return;
    const previousTasks = tasks;
    const orderById = new Map(taskIds.map((id, idx) => [id, idx]));
    setTasks((prev) => prev.map((t) => (orderById.has(t._id) ? { ...t, status, order: orderById.get(t._id)! } : t)));
    try {
      const updated = await reorderStandaloneTasks(projectId, status, taskIds);
      setTasks((prev) => prev.map((t) => updated.find((u) => u._id === t._id) ?? t));
    } catch (err) {
      setTasks(previousTasks);
      setError(standaloneErrorMessage(err) ?? 'Failed to reorder tasks.');
    }
  };

  // Dropped on empty column space (not on a specific card) — append to the end of that column.
  const handleColumnDrop = (status: string) => {
    const taskId = draggingTaskId;
    clearDragState();
    if (!taskId) return;
    const newOrder = columnTaskIds(status).filter((id) => id !== taskId);
    newOrder.push(taskId);
    applyReorder(status, newOrder);
  };

  const handleCardDragOver = (e: React.DragEvent, taskId: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (taskId === draggingTaskId) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setDragOverTaskId(taskId);
    setDragOverPosition(e.clientY - rect.top > rect.height / 2 ? 'after' : 'before');
  };

  // Dropped directly on another card — insert the dragged task immediately before/after it.
  const handleCardDrop = (e: React.DragEvent, status: string, targetTaskId: string) => {
    e.preventDefault();
    e.stopPropagation();
    const taskId = draggingTaskId;
    const position = dragOverPosition ?? 'before';
    clearDragState();
    if (!taskId || taskId === targetTaskId) return;

    const withoutDragged = columnTaskIds(status).filter((id) => id !== taskId);
    const targetIdx = withoutDragged.indexOf(targetTaskId);
    const insertAt = targetIdx === -1 ? withoutDragged.length : position === 'after' ? targetIdx + 1 : targetIdx;
    const newOrder = [...withoutDragged];
    newOrder.splice(insertAt, 0, taskId);
    applyReorder(status, newOrder);
  };

  const startEditProject = () => {
    if (!project) return;
    setEditName(project.name);
    setEditDescription(project.description ?? '');
    setIsEditingProject(true);
  };

  const handleSaveProject = async () => {
    if (!projectId || !editName.trim()) return;
    setIsSavingProject(true);
    try {
      const updated = await updateStandaloneProject(projectId, {
        name: editName.trim(),
        description: editDescription.trim() || undefined,
      });
      setProject(updated);
      setIsEditingProject(false);
    } catch {
      setError('Failed to update project.');
    } finally {
      setIsSavingProject(false);
    }
  };

  const handleDeleteProject = async () => {
    if (!projectId) return;
    if (!window.confirm('Delete this project and all its tasks? This cannot be undone.')) return;
    setIsDeletingProject(true);
    try {
      await deleteStandaloneProject(projectId);
      await invalidateStandaloneProjectsCache();
      navigate('/standalone');
    } catch {
      setError('Failed to delete project.');
      setIsDeletingProject(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-base-200 flex items-center justify-center">
        <span className="loading loading-spinner loading-lg opacity-40" />
      </div>
    );
  }

  if (error && !project) {
    return (
      <div className="min-h-screen bg-base-200 flex items-center justify-center">
        <div className="alert alert-error max-w-sm">
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base-200">
      <div className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2 min-w-0">
            <Link to="/standalone" className="btn btn-ghost btn-sm btn-circle shrink-0 mt-0.5">
              <LuArrowLeft size={16} />
            </Link>
            <div className="min-w-0">
              {isEditingProject ? (
                <div className="space-y-2 max-w-md">
                  <input
                    className="input input-bordered input-sm w-full"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                  />
                  <textarea
                    className="textarea textarea-bordered textarea-sm w-full"
                    rows={2}
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                  />
                  <div className="flex gap-2">
                    <button
                      className="btn btn-ghost btn-xs"
                      onClick={() => setIsEditingProject(false)}
                      disabled={isSavingProject}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary btn-xs"
                      onClick={handleSaveProject}
                      disabled={isSavingProject || !editName.trim()}
                    >
                      {isSavingProject && <LuLoaderPinwheel className="animate-spin" size={12} />}
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    {project?.key && <span className="badge badge-ghost badge-sm font-mono">{project.key}</span>}
                    <span className="text-2xl font-semibold truncate">{project?.name}</span>
                    <button className="btn btn-ghost btn-xs btn-circle" onClick={startEditProject} title="Edit project">
                      <LuPencil size={14} />
                    </button>
                  </div>
                  {project?.description && <div className="hidden md:block text-sm opacity-70">{project.description}</div>}
                </>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              className="btn btn-primary btn-sm shadow-glow gap-1.5"
              onClick={() =>
                openChat({
                  projectId,
                  connectedAgents: ['StandaloneAgent'],
                  suggestedPrompts: [
                    'What should I focus on next in this project?',
                    'Which tasks are overdue?',
                    'Summarize the current status',
                  ],
                  analyticsContext: { scope: 'project', projectIds: projectId ? [projectId] : undefined },
                })
              }
            >
              <LuBot size={14} />
              Ask mFlow
            </button>
            <ActionsMenu
              items={[
                { label: 'Analytics', icon: LuChartBar, to: `/dashboard/projects/${projectId}` },
                { label: 'Knowledge', icon: LuBookOpenText, to: `/dashboard/projects/${projectId}/knowledge` },
                { label: 'Board settings', icon: LuSettings, onClick: () => setIsSettingsOpen(true) },
                { label: 'Transfer…', icon: LuArrowRightLeft, onClick: () => setIsTransferModalOpen(true) },
                {
                  label: 'Delete project',
                  icon: LuTrash2,
                  onClick: handleDeleteProject,
                  disabled: isDeletingProject,
                  destructive: true,
                },
              ]}
            />
          </div>
        </div>

        {error && (
          <div className="alert alert-error">
            <span>{error}</span>
          </div>
        )}

        {/* TEMP client-side search — see the state comment above `search` for why. */}
        <div className="flex items-center gap-2">
          <label className="input input-bordered input-sm flex items-center gap-2 max-w-xs">
            <LuSearch size={13} className="opacity-40" />
            <input
              className="grow"
              placeholder="Search tasks…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {isSearching && (
              <button type="button" onClick={() => setSearch('')} aria-label="Clear search">
                <LuX size={13} className="opacity-50" />
              </button>
            )}
          </label>
          {isSearching && (
            <span className="text-xs opacity-50">
              {matchCount} match{matchCount === 1 ? '' : 'es'} · drag-and-drop disabled while searching
            </span>
          )}
        </div>

        {/* Board — a horizontally scrolling row, since the number of columns is per-project custom
            now (statuses can be added/removed), not a fixed 3-up grid. */}
        <div className="flex gap-4 overflow-x-auto pb-2">
          {statuses.map((col) => {
            const colTasks = tasks
              .filter((t) => t.status === col.id && matchesSearch(t))
              .sort((a, b) => a.order - b.order);
            return (
              <div
                key={col.id}
                className="bg-base-100 rounded-xl shadow-card p-3 space-y-2 min-h-[200px] w-72 shrink-0"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  handleColumnDrop(col.id);
                }}
              >
                <div className="flex items-center justify-between px-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ backgroundColor: col.color || '#9ca3af' }}
                    />
                    <div className="font-semibold text-sm truncate">{col.name}</div>
                    {col.agentRole && (
                      <span
                        className="badge badge-primary badge-xs shrink-0"
                        title={
                          col.agentRole === 'working'
                            ? 'The coding agent moves a task here when it starts running.'
                            : 'Where a task moves when the coding agent finishes.'
                        }
                      >
                        {col.agentRole === 'working' ? 'Working' : 'Review'}
                      </span>
                    )}
                  </div>
                  <span className="badge badge-ghost badge-sm shrink-0">{colTasks.length}</span>
                </div>

                <div className="space-y-2">
                  {colTasks.map((task) => {
                    const stats = subtaskStats.get(task._id);
                    return (
                      <TaskCard
                        key={task._id}
                        task={task}
                        labels={project?.labels ?? []}
                        subtaskTotal={stats?.total ?? 0}
                        subtaskDone={stats?.done ?? 0}
                        isDoneCategory={col.category === 'done'}
                        isDragging={draggingTaskId === task._id}
                        dropIndicator={dragOverTaskId === task._id ? dragOverPosition : null}
                        onClick={() => openEditModal(task)}
                        onDragStart={(e) => {
                          if (isSearching) {
                            e.preventDefault();
                            return;
                          }
                          setDraggingTaskId(task._id);
                          e.dataTransfer.effectAllowed = 'move';
                        }}
                        onDragEnd={clearDragState}
                        onCardDragOver={(e) => handleCardDragOver(e, task._id)}
                        onCardDrop={(e) => handleCardDrop(e, col.id, task._id)}
                      />
                    );
                  })}
                  {colTasks.length === 0 && <div className="text-xs opacity-40 text-center py-4">No tasks</div>}
                </div>

                <button
                  className="btn btn-ghost btn-sm w-full gap-1 justify-start opacity-60 hover:opacity-100"
                  onClick={() => openCreateModal(col.id)}
                >
                  <LuPlus size={14} /> Add task
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {isModalOpen && isLoadingModalTask && (
        <Modal onClose={() => setIsModalOpen(false)}>
          <div className="card-body p-10 items-center justify-center">
            <LuLoaderPinwheel className="animate-spin opacity-40" size={24} />
          </div>
        </Modal>
      )}

      {isModalOpen && !isLoadingModalTask && project && (
        <TaskModal
          project={project}
          tasks={tasks}
          task={modalTask}
          defaultStatus={modalDefaultStatus || statuses[0]?.id}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSaveTask}
          onDelete={modalTask ? handleDeleteTask : undefined}
          onQuickAddSubtask={modalTask ? handleQuickAddSubtask : undefined}
        />
      )}

      {isSettingsOpen && project && (
        <BoardSettingsModal project={project} onClose={() => setIsSettingsOpen(false)} onUpdated={setProject} />
      )}

      {isTransferModalOpen && projectId && (
        <TransferModal
          sourceProvider="standalone"
          sourceProjectId={projectId}
          sourceProjectName={project?.name}
          onClose={() => setIsTransferModalOpen(false)}
        />
      )}
    </div>
  );
};

export default StandaloneBoardPage;
